import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import api from "../api/api";

import {
    useAcademicYear,
} from "../AcademicYearContext";

import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Empty,
    Input,
    Pagination,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    Upload,
    message,
} from "antd";

import {
    Archive,
    ArchiveRestore,
    BarChart3,
    CalendarDays,
    CheckCircle2,
    Clock3,
    Download,
    Edit3,
    Eye,
    FileArchive,
    FileText,
    Filter,
    FolderArchive,
    HardDrive,
    History,
    Plus,
    RefreshCw,
    Search,
    ShieldAlert,
    Trash2,
    UploadCloud,
} from "lucide-react";

import Swal from "sweetalert2";

import ArchiveDocumentForm from "./ArchiveDocumentForm";
import ArchiveDocumentDetails from "./ArchiveDocumentDetails";

import "./SchoolArchiveDashboard.css";


const {
    Title,
    Text,
} = Typography;

const {
    Option,
} = Select;


// ============================================================
// CONSTANTS
// ============================================================

const PAGE_SIZE = 25;

const DEFAULT_SUMMARY = {
    total_documents: 0,
    active_documents: 0,
    archived_documents: 0,
    duplicate_documents: 0,
    sensitive_documents: 0,
    expired_documents: 0,
};


// ============================================================
// HELPERS
// ============================================================

const normalizeListResponse = (
    data
) => {

    if (
        Array.isArray(data)
    ) {
        return {
            results: data,
            count: data.length,
            total_pages: 1,
            page: 1,
        };
    }

    return {
        results: Array.isArray(
            data?.results
        )
            ? data.results
            : [],

        count:
            Number(
                data?.pagination?.count
            ) || 0,

        total_pages:
            Number(
                data?.pagination?.total_pages
            ) || 1,

        page:
            Number(
                data?.pagination?.page
            ) || 1,
    };
};


const getErrorMessage = (
    error,
    fallback = "Something went wrong."
) => {

    const data =
        error?.response?.data;

    if (
        typeof data === "string"
        && data.trim()
    ) {
        return data;
    }

    if (
        data?.detail
    ) {
        return data.detail;
    }

    if (
        data?.message
    ) {
        return data.message;
    }

    if (
        typeof data === "object"
        && data
    ) {

        const firstError =
            Object.values(data)
                .flat()
                .find(
                    value =>
                        typeof value === "string"
                );

        if (firstError) {
            return firstError;
        }
    }

    return (
        error?.message
        || fallback
    );
};


const formatFileSize = (
    bytes
) => {

    const size =
        Number(bytes) || 0;

    if (size <= 0) {
        return "0 B";
    }

    if (size < 1024) {
        return `${size} B`;
    }

    if (size < 1024 * 1024) {
        return `${(
            size / 1024
        ).toFixed(1)} KB`;
    }

    if (
        size
        < 1024 * 1024 * 1024
    ) {
        return `${(
            size
            / (1024 * 1024)
        ).toFixed(1)} MB`;
    }

    return `${(
        size
        / (1024 * 1024 * 1024)
    ).toFixed(2)} GB`;
};


const getCategoryLabel = (
    category
) => {

    const labels = {
        student:
            "Student Records",

        teacher:
            "Teacher Records",

        administrative:
            "Administrative",

        academic:
            "Academic",

        financial:
            "Financial",

        hr:
            "Human Resources",

        legal:
            "Legal / Compliance",

        general:
            "General School",
    };

    return (
        labels[category]
        || category
        || "General"
    );
};


const getCategoryColor = (
    category
) => {

    const colors = {
        student: "blue",
        teacher: "purple",
        administrative: "cyan",
        academic: "geekblue",
        financial: "green",
        hr: "orange",
        legal: "red",
        general: "default",
    };

    return (
        colors[category]
        || "default"
    );
};


// ============================================================
// COMPONENT
// ============================================================

const SchoolArchiveDashboard = () => {

    const {
        selectedYear,
        initialized: academicYearInitialized,
    } = useAcademicYear();


    // ========================================================
    // STATE
    // ========================================================

    const [
        documents,
        setDocuments,
    ] = useState([]);

    const [
        summary,
        setSummary,
    ] = useState(
        DEFAULT_SUMMARY
    );

    const [
        categoryBreakdown,
        setCategoryBreakdown,
    ] = useState([]);

    const [
        yearBreakdown,
        setYearBreakdown,
    ] = useState([]);

    const [
        documentTypes,
        setDocumentTypes,
    ] = useState([]);


    const [
        loading,
        setLoading,
    ] = useState(false);

    const [
        summaryLoading,
        setSummaryLoading,
    ] = useState(false);

    const [
        documentTypesLoading,
        setDocumentTypesLoading,
    ] = useState(false);


    const [
        search,
        setSearch,
    ] = useState("");

    const [
        searchInput,
        setSearchInput,
    ] = useState("");


    const [
        category,
        setCategory,
    ] = useState("");

    const [
        documentType,
        setDocumentType,
    ] = useState("");

    const [
        statusFilter,
        setStatusFilter,
    ] = useState("active");


    const [
        currentPage,
        setCurrentPage,
    ] = useState(1);

    const [
        totalDocuments,
        setTotalDocuments,
    ] = useState(0);

    const [
        totalPages,
        setTotalPages,
    ] = useState(1);


    const [
        formOpen,
        setFormOpen,
    ] = useState(false);

    const [
        editingDocument,
        setEditingDocument,
    ] = useState(null);


    const [
        detailsOpen,
        setDetailsOpen,
    ] = useState(false);

    const [
        selectedDocument,
        setSelectedDocument,
    ] = useState(null);


    const [
        refreshing,
        setRefreshing,
    ] = useState(false);


    // ========================================================
    // ACADEMIC YEAR ID
    // ========================================================

    const selectedYearId =
        selectedYear?.id
        || null;


    // ========================================================
    // FETCH DOCUMENT TYPES
    // ========================================================

    const fetchDocumentTypes =
        useCallback(
            async () => {

                setDocumentTypesLoading(
                    true
                );

                try {

                    const response =
                        await api.get(
                            "/document-types/",
                            {
                                params: {
                                    page_size: 100,
                                    active: "true",
                                    ordering: "name",
                                },
                            }
                        );

                    const normalized =
                        normalizeListResponse(
                            response.data
                        );

                    setDocumentTypes(
                        normalized.results
                    );

                } catch (error) {

                    console.error(
                        "Archive document types error:",
                        error
                    );

                    message.error(
                        getErrorMessage(
                            error,
                            "Unable to load document types."
                        )
                    );

                } finally {

                    setDocumentTypesLoading(
                        false
                    );

                }

            },
            []
        );


    // ========================================================
    // FETCH DOCUMENTS
    // ========================================================

    const fetchDocuments =
        useCallback(
            async ({
                silent = false,
                page = currentPage,
            } = {}) => {

                if (
                    !selectedYearId
                ) {

                    setDocuments([]);

                    setTotalDocuments(0);

                    setTotalPages(1);

                    return;
                }

                if (!silent) {

                    setLoading(true);

                }

                try {

                    const params = {
                        academic_year:
                            selectedYearId,

                        page,

                        page_size:
                            PAGE_SIZE,

                        variant:
                            "list",

                        ordering:
                            "-created_at",
                    };


                    if (
                        search.trim()
                    ) {

                        params.search =
                            search.trim();

                    }


                    if (category) {

                        params.category =
                            category;

                    }


                    if (documentType) {

                        params.document_type =
                            documentType;

                    }


                    if (
                        statusFilter
                        === "active"
                    ) {

                        params.active =
                            "true";

                        params.archived =
                            "false";

                    } else if (
                        statusFilter
                        === "archived"
                    ) {

                        params.archived =
                            "true";

                    } else if (
                        statusFilter
                        === "all"
                    ) {

                        // No status restrictions.

                    }


                    const response =
                        await api.get(
                            "/documents/",
                            {
                                params,
                            }
                        );


                    const normalized =
                        normalizeListResponse(
                            response.data
                        );


                    setDocuments(
                        normalized.results
                    );

                    setTotalDocuments(
                        normalized.count
                    );

                    setTotalPages(
                        normalized.total_pages
                    );

                    setCurrentPage(
                        normalized.page
                    );

                } catch (error) {

                    console.error(
                        "Archive documents error:",
                        error
                    );

                    message.error(
                        getErrorMessage(
                            error,
                            "Unable to load archive documents."
                        )
                    );

                    setDocuments([]);

                } finally {

                    if (!silent) {

                        setLoading(false);

                    }

                }

            },
            [
                selectedYearId,
                search,
                category,
                documentType,
                statusFilter,
                currentPage,
            ]
        );


    // ========================================================
    // FETCH SUMMARY
    // ========================================================

    const fetchSummary =
        useCallback(
            async () => {

                if (
                    !selectedYearId
                ) {

                    setSummary(
                        DEFAULT_SUMMARY
                    );

                    setCategoryBreakdown([]);

                    setYearBreakdown([]);

                    return;
                }

                setSummaryLoading(
                    true
                );

                try {

                    const response =
                        await api.get(
                            "/documents/summary/",
                            {
                                params: {
                                    academic_year:
                                        selectedYearId,
                                },
                            }
                        );


                    const data =
                        response.data
                        || {};


                    setSummary(
                        {
                            ...DEFAULT_SUMMARY,
                            ...(data.summary || {}),
                        }
                    );


                    setCategoryBreakdown(
                        Array.isArray(
                            data.by_category
                        )
                            ? data.by_category
                            : []
                    );


                    setYearBreakdown(
                        Array.isArray(
                            data.by_academic_year
                        )
                            ? data.by_academic_year
                            : []
                    );

                } catch (error) {

                    console.error(
                        "Archive summary error:",
                        error
                    );

                    message.error(
                        getErrorMessage(
                            error,
                            "Unable to load archive summary."
                        )
                    );

                } finally {

                    setSummaryLoading(
                        false
                    );

                }

            },
            [
                selectedYearId,
            ]
        );


    // ========================================================
    // INITIAL LOAD
    // ========================================================

    useEffect(() => {

        if (
            !academicYearInitialized
        ) {
            return;
        }

        fetchDocumentTypes();

    }, [
        academicYearInitialized,
        fetchDocumentTypes,
    ]);


    // ========================================================
    // YEAR CHANGE / FILTER CHANGE
    // ========================================================

    useEffect(() => {

        if (
            !academicYearInitialized
        ) {
            return;
        }

        setCurrentPage(1);

    }, [
        selectedYearId,
        search,
        category,
        documentType,
        statusFilter,
        academicYearInitialized,
    ]);


    useEffect(() => {

        if (
            !academicYearInitialized
        ) {
            return;
        }

        fetchDocuments({
            page: currentPage,
        });

    }, [
        currentPage,
        selectedYearId,
        search,
        category,
        documentType,
        statusFilter,
        academicYearInitialized,
        fetchDocuments,
    ]);


    useEffect(() => {

        if (
            !academicYearInitialized
        ) {
            return;
        }

        fetchSummary();

    }, [
        selectedYearId,
        academicYearInitialized,
        fetchSummary,
    ]);


    // ========================================================
    // SEARCH DEBOUNCE
    // ========================================================

    useEffect(() => {

        const timer =
            setTimeout(() => {

                setSearch(
                    searchInput
                );

            }, 450);

        return () => {
            clearTimeout(timer);
        };

    }, [
        searchInput,
    ]);


    // ========================================================
    // REFRESH EVERYTHING
    // ========================================================

    const refreshAll =
        useCallback(
            async () => {

                setRefreshing(true);

                try {

                    await Promise.all([
                        fetchDocuments({
                            page: currentPage,
                        }),
                        fetchSummary(),
                        fetchDocumentTypes(),
                    ]);

                } finally {

                    setRefreshing(false);

                }

            },
            [
                currentPage,
                fetchDocuments,
                fetchSummary,
                fetchDocumentTypes,
            ]
        );


    // ========================================================
    // CREATE
    // ========================================================

    const handleCreate =
        () => {

            setEditingDocument(
                null
            );

            setFormOpen(true);

        };


    // ========================================================
    // EDIT
    // ========================================================

    const handleEdit =
        async (
            document
        ) => {

            try {

                const response =
                    await api.get(
                        `/documents/${document.id}/`
                    );

                setEditingDocument(
                    response.data
                );

                setFormOpen(true);

            } catch (error) {

                Swal.fire(
                    {
                        icon: "error",
                        title: "Unable to edit",
                        text: getErrorMessage(
                            error,
                            "The document could not be loaded."
                        ),
                    }
                );

            }

        };


    // ========================================================
    // VIEW DETAILS
    // ========================================================

    const handleView =
        async (
            document
        ) => {

            try {

                const response =
                    await api.get(
                        `/documents/${document.id}/`
                    );

                setSelectedDocument(
                    response.data
                );

                setDetailsOpen(
                    true
                );

            } catch (error) {

                Swal.fire(
                    {
                        icon: "error",
                        title: "Unable to open document",
                        text: getErrorMessage(
                            error,
                            "The document could not be loaded."
                        ),
                    }
                );

            }

        };


    // ========================================================
    // DOWNLOAD
    // ========================================================

const handleDownload = async (archiveDocument) => {
    try {
        const response = await api.get(
            `/documents/${archiveDocument.id}/download/`,
            {
                responseType: "blob",
            }
        );

        const blob = new Blob(
            [response.data],
            {
                type:
                    response.headers["content-type"] ||
                    archiveDocument.mime_type ||
                    "application/octet-stream",
            }
        );

        const url = window.URL.createObjectURL(blob);

        const link = window.document.createElement("a");

        link.href = url;

        link.download =
            archiveDocument.original_filename ||
            `${archiveDocument.reference_number}.${archiveDocument.file_extension || "file"}`;

        window.document.body.appendChild(link);

        link.click();

        window.document.body.removeChild(link);

        window.URL.revokeObjectURL(url);

    } catch (error) {
        console.error("Archive document download error:", error);

        Swal.fire({
            icon: "error",
            title: "Download failed",
            text: getErrorMessage(
                error,
                "The document could not be downloaded."
            ),
        });
    }
};


    // ========================================================
    // ARCHIVE
    // ========================================================

    const handleArchive =
        async (
            document
        ) => {

            const result =
                await Swal.fire(
                    {
                        icon: "warning",
                        title: "Archive document?",
                        html: `
                            <p>
                                You are about to archive:
                            </p>
                            <strong>
                                ${document.title}
                            </strong>
                            <p style="margin-top:10px;">
                                The document will no longer
                                appear in the active archive.
                            </p>
                        `,
                        showCancelButton:
                            true,
                        confirmButtonText:
                            "Yes, archive it",
                        cancelButtonText:
                            "Cancel",
                        reverseButtons:
                            true,
                    }
                );


            if (
                !result.isConfirmed
            ) {
                return;
            }


            try {

                Swal.fire(
                    {
                        title:
                            "Archiving...",
                        text:
                            "Please wait.",
                        allowOutsideClick:
                            false,
                        allowEscapeKey:
                            false,
                        didOpen: () => {
                            Swal.showLoading();
                        },
                    }
                );


                await api.post(
                    `/documents/${document.id}/archive/`
                );


                await Swal.fire(
                    {
                        icon:
                            "success",
                        title:
                            "Archived",
                        text:
                            "The document was archived successfully.",
                        timer:
                            1800,
                        showConfirmButton:
                            false,
                    }
                );


                await refreshAll();

            } catch (error) {

                Swal.fire(
                    {
                        icon:
                            "error",
                        title:
                            "Archive failed",
                        text:
                            getErrorMessage(
                                error,
                                "The document could not be archived."
                            ),
                    }
                );

            }

        };


    // ========================================================
    // RESTORE
    // ========================================================

    const handleRestore =
        async (
            document
        ) => {

            const result =
                await Swal.fire(
                    {
                        icon:
                            "question",
                        title:
                            "Restore document?",
                        text:
                            `Restore "${document.title}" to the active archive?`,
                        showCancelButton:
                            true,
                        confirmButtonText:
                            "Yes, restore",
                        cancelButtonText:
                            "Cancel",
                        reverseButtons:
                            true,
                    }
                );


            if (
                !result.isConfirmed
            ) {
                return;
            }


            try {

                Swal.fire(
                    {
                        title:
                            "Restoring...",
                        allowOutsideClick:
                            false,
                        didOpen: () => {
                            Swal.showLoading();
                        },
                    }
                );


                await api.post(
                    `/documents/${document.id}/restore/`
                );


                await Swal.fire(
                    {
                        icon:
                            "success",
                        title:
                            "Restored",
                        text:
                            "The document was restored successfully.",
                        timer:
                            1800,
                        showConfirmButton:
                            false,
                    }
                );


                await refreshAll();

            } catch (error) {

                Swal.fire(
                    {
                        icon:
                            "error",
                        title:
                            "Restore failed",
                        text:
                            getErrorMessage(
                                error,
                                "The document could not be restored."
                            ),
                    }
                );

            }

        };


    // ========================================================
    // DELETE / SOFT DELETE
    // ========================================================

    const handleDelete =
        async (
            document
        ) => {

            const result =
                await Swal.fire(
                    {
                        icon:
                            "warning",
                        title:
                            "Delete archive record?",
                        html: `
                            <div style="text-align:left;">
                                <p>
                                    You are about to deactivate:
                                </p>

                                <p>
                                    <strong>
                                        ${document.title}
                                    </strong>
                                </p>

                                <p>
                                    Reference:
                                    <strong>
                                        ${document.reference_number}
                                    </strong>
                                </p>

                                <p style="color:#b91c1c;">
                                    This is a protected soft-delete.
                                    The archive record will be
                                    deactivated rather than permanently
                                    destroyed.
                                </p>
                            </div>
                        `,
                        showCancelButton:
                            true,
                        confirmButtonText:
                            "Yes, delete",
                        cancelButtonText:
                            "Cancel",
                        confirmButtonColor:
                            "#dc2626",
                        reverseButtons:
                            true,
                        focusCancel:
                            true,
                    }
                );


            if (
                !result.isConfirmed
            ) {
                return;
            }


            try {

                Swal.fire(
                    {
                        title:
                            "Deleting...",
                        text:
                            "Please wait.",
                        allowOutsideClick:
                            false,
                        allowEscapeKey:
                            false,
                        didOpen: () => {
                            Swal.showLoading();
                        },
                    }
                );


                await api.delete(
                    `/documents/${document.id}/`
                );


                await Swal.fire(
                    {
                        icon:
                            "success",
                        title:
                            "Deleted",
                        text:
                            "The archive record was deactivated successfully.",
                        timer:
                            1800,
                        showConfirmButton:
                            false,
                    }
                );


                await refreshAll();

            } catch (error) {

                Swal.fire(
                    {
                        icon:
                            "error",
                        title:
                            "Delete failed",
                        text:
                            getErrorMessage(
                                error,
                                "The archive record could not be deleted."
                            ),
                    }
                );

            }

        };


    // ========================================================
    // FORM SUCCESS
    // ========================================================

    const handleFormSuccess =
        async (
            savedDocument
        ) => {

            setFormOpen(
                false
            );

            setEditingDocument(
                null
            );


            await refreshAll();


            Swal.fire(
                {
                    icon:
                        "success",
                    title:
                        editingDocument
                            ? "Document updated"
                            : "Document uploaded",
                    text:
                        editingDocument
                            ? "The archive document was updated successfully."
                            : "The archive document was uploaded successfully.",
                    timer:
                        1800,
                    showConfirmButton:
                        false,
                }
            );

        };


    // ========================================================
    // CATEGORY OPTIONS
    // ========================================================

    const categoryOptions =
        useMemo(
            () => [

                {
                    value:
                        "student",
                    label:
                        "Student Records",
                },

                {
                    value:
                        "teacher",
                    label:
                        "Teacher Records",
                },

                {
                    value:
                        "administrative",
                    label:
                        "Administrative",
                },

                {
                    value:
                        "academic",
                    label:
                        "Academic",
                },

                {
                    value:
                        "financial",
                    label:
                        "Financial",
                },

                {
                    value:
                        "hr",
                    label:
                        "Human Resources",
                },

                {
                    value:
                        "legal",
                    label:
                        "Legal / Compliance",
                },

                {
                    value:
                        "general",
                    label:
                        "General School",
                },

            ],
            []
        );


    // ========================================================
    // TABLE COLUMNS
    // ========================================================

    const columns =
        useMemo(
            () => [

                {
                    title:
                        "Document",
                    key:
                        "document",
                    width:
                        320,
                    render:
                        (_, record) => (

                            <div className="archive-document-cell">

                                <div className="archive-file-icon">

                                    <FileText
                                        size={21}
                                    />

                                </div>

                                <div
                                    className="archive-document-info"
                                >

                                    <Tooltip
                                        title={
                                            record.title
                                        }
                                    >

                                        <Text
                                            strong
                                            className="archive-document-title"
                                        >
                                            {
                                                record.title
                                            }
                                        </Text>

                                    </Tooltip>

                                    <Text
                                        type="secondary"
                                        className="archive-reference"
                                    >
                                        {
                                            record.reference_number
                                        }
                                    </Text>

                                </div>

                            </div>

                        ),
                },


                {
                    title:
                        "Category",
                    dataIndex:
                        "archive_category",
                    key:
                        "archive_category",
                    width:
                        150,
                    render:
                        value => (

                            <Tag
                                color={
                                    getCategoryColor(
                                        value
                                    )
                                }
                            >
                                {
                                    getCategoryLabel(
                                        value
                                    )
                                }
                            </Tag>

                        ),
                },


                {
                    title:
                        "Type",
                    dataIndex:
                        "document_type_name",
                    key:
                        "document_type_name",
                    width:
                        170,
                    render:
                        value =>
                            value
                            || "—",
                },


                {
                    title:
                        "File",
                    key:
                        "file",
                    width:
                        120,
                    render:
                        (_, record) => (

                            <div>

                                <Tag>
                                    {
                                        (
                                            record.file_extension
                                            || "FILE"
                                        ).toUpperCase()
                                    }
                                </Tag>

                                <div>
                                    <Text
                                        type="secondary"
                                        className="archive-file-size"
                                    >
                                        {
                                            formatFileSize(
                                                record.file_size
                                            )
                                        }
                                    </Text>
                                </div>

                            </div>

                        ),
                },


                {
                    title:
                        "Security",
                    key:
                        "security",
                    width:
                        145,
                    render:
                        (_, record) => (

                            <Space
                                direction="vertical"
                                size={2}
                            >

                                <Tag
                                    color={
                                        record.confidentiality
                                        === "restricted"
                                            ? "red"
                                            : record.confidentiality
                                            === "confidential"
                                            ? "orange"
                                            : record.confidentiality
                                            === "internal"
                                            ? "blue"
                                            : "green"
                                    }
                                >
                                    {
                                        record.confidentiality
                                        || "internal"
                                    }
                                </Tag>

                                {
                                    record.is_sensitive
                                        ? (
                                            <Tag
                                                color="volcano"
                                                icon={
                                                    <ShieldAlert
                                                        size={12}
                                                    />
                                                }
                                            >
                                                Sensitive
                                            </Tag>
                                        )
                                        : null
                                }

                            </Space>

                        ),
                },


                {
                    title:
                        "Status",
                    key:
                        "status",
                    width:
                        125,
                    render:
                        (_, record) => {

                            if (
                                record.is_archived
                            ) {

                                return (
                                    <Tag
                                        color="gold"
                                        icon={
                                            <Archive
                                                size={12}
                                            />
                                        }
                                    >
                                        Archived
                                    </Tag>
                                );

                            }

                            if (
                                record.is_duplicate
                            ) {

                                return (
                                    <Tag
                                        color="orange"
                                    >
                                        Duplicate
                                    </Tag>
                                );

                            }

                            return (
                                <Tag
                                    color="green"
                                    icon={
                                        <CheckCircle2
                                            size={12}
                                        />
                                    }
                                >
                                    Active
                                </Tag>
                            );

                        },
                },


                {
                    title:
                        "Version",
                    dataIndex:
                        "version",
                    key:
                        "version",
                    width:
                        90,
                    render:
                        value => (
                            <Tag>
                                v{value || 1}
                            </Tag>
                        ),
                },


                {
                    title:
                        "Actions",
                    key:
                        "actions",
                    fixed:
                        "right",
                    width:
                        210,
                    render:
                        (_, record) => (

                            <Space
                                size={4}
                                wrap
                            >

                                <Tooltip
                                    title="View details"
                                >

                                    <Button
                                        size="small"
                                        icon={
                                            <Eye
                                                size={15}
                                            />
                                        }
                                        onClick={() =>
                                            handleView(
                                                record
                                            )
                                        }
                                    />

                                </Tooltip>


                                <Tooltip
                                    title="Edit"
                                >

                                    <Button
                                        size="small"
                                        icon={
                                            <Edit3
                                                size={15}
                                            />
                                        }
                                        onClick={() =>
                                            handleEdit(
                                                record
                                            )
                                        }
                                    />

                                </Tooltip>


                                <Tooltip
                                    title="Download"
                                >

                                    <Button
                                        size="small"
                                        icon={
                                            <Download
                                                size={15}
                                            />
                                        }
                                        onClick={() =>
                                            handleDownload(
                                                record
                                            )
                                        }
                                    />

                                </Tooltip>


                                {
                                    record.is_archived
                                        ? (

                                            <Tooltip
                                                title="Restore"
                                            >

                                                <Button
                                                    size="small"
                                                    type="primary"
                                                    ghost
                                                    icon={
                                                        <ArchiveRestore
                                                            size={15}
                                                        />
                                                    }
                                                    onClick={() =>
                                                        handleRestore(
                                                            record
                                                        )
                                                    }
                                                />

                                            </Tooltip>

                                        )
                                        : (

                                            <Tooltip
                                                title="Archive"
                                            >

                                                <Button
                                                    size="small"
                                                    icon={
                                                        <Archive
                                                            size={15}
                                                        />
                                                    }
                                                    onClick={() =>
                                                        handleArchive(
                                                            record
                                                        )
                                                    }
                                                />

                                            </Tooltip>

                                        )
                                }


                                {
                                    record.is_active
                                        ? (

                                            <Tooltip
                                                title="Delete"
                                            >

                                                <Button
                                                    size="small"
                                                    danger
                                                    icon={
                                                        <Trash2
                                                            size={15}
                                                        />
                                                    }
                                                    onClick={() =>
                                                        handleDelete(
                                                            record
                                                        )
                                                    }
                                                />

                                            </Tooltip>

                                        )
                                        : null
                                }

                            </Space>

                        ),
                },

            ],
            []
        );


    // ========================================================
    // EMPTY / YEAR GUARD
    // ========================================================

    if (
        academicYearInitialized
        && !selectedYear
    ) {

        return (

            <div className="school-archive-page">

                <Card
                    className="archive-empty-year"
                >

                    <Empty
                        image={
                            <CalendarDays
                                size={64}
                            />
                        }
                        description={
                            <Space
                                direction="vertical"
                            >

                                <Title
                                    level={4}
                                >
                                    No Academic Year Selected
                                </Title>

                                <Text
                                    type="secondary"
                                >
                                    Please select an academic
                                    year before using the
                                    School Archive.
                                </Text>

                            </Space>
                        }
                    />

                </Card>

            </div>

        );

    }


    // ========================================================
    // RENDER
    // ========================================================

    return (

        <div className="school-archive-page">

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="archive-page-header">

                <div>

                    <div className="archive-title-row">

                        <div className="archive-title-icon">

                            <FolderArchive
                                size={28}
                            />

                        </div>

                        <div>

                            <Title
                                level={2}
                                className="archive-page-title"
                            >
                                School Archive
                            </Title>

                            <Text
                                type="secondary"
                            >
                                Securely manage and preserve
                                school records.
                            </Text>

                        </div>

                    </div>

                </div>


                <Space
                    wrap
                    className="archive-header-actions"
                >

                    <Tag
                        color="blue"
                        icon={
                            <CalendarDays
                                size={13}
                            />
                        }
                    >
                        Academic Year:{" "}
                        {
                            selectedYear?.name
                            || "—"
                        }
                    </Tag>


                    <Button
                        icon={
                            <RefreshCw
                                size={16}
                            />
                        }
                        loading={
                            refreshing
                        }
                        onClick={
                            refreshAll
                        }
                    >
                        Refresh
                    </Button>


                    <Button
                        type="primary"
                        icon={
                            <Plus
                                size={17}
                            />
                        }
                        onClick={
                            handleCreate
                        }
                    >
                        Add Document
                    </Button>

                </Space>

            </div>


            {/* =================================================
                YEAR CONTEXT BANNER
            ================================================= */}

            <Alert
                className="archive-year-banner"
                type="info"
                showIcon
                icon={
                    <CalendarDays
                        size={18}
                    />
                }
                message={
                    <strong>
                        Archive filtered by academic year
                    </strong>
                }
                description={
                    <>
                        All archive requests on this page
                        are currently scoped to{" "}
                        <strong>
                            {
                                selectedYear?.name
                            }
                        </strong>
                        . Change the global academic-year
                        selector to view another year's records.
                    </>
                }
            />


            {/* =================================================
                STATISTICS
            ================================================= */}

            <Row
                gutter={[
                    16,
                    16,
                ]}
                className="archive-statistics"
            >

                <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={4}
                >

                    <Card
                        loading={
                            summaryLoading
                        }
                        className="archive-stat-card"
                    >

                        <Statistic
                            title="Total Documents"
                            value={
                                summary.total_documents
                            }
                            prefix={
                                <FileArchive
                                    size={21}
                                />
                            }
                        />

                    </Card>

                </Col>


                <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={4}
                >

                    <Card
                        loading={
                            summaryLoading
                        }
                        className="archive-stat-card"
                    >

                        <Statistic
                            title="Active"
                            value={
                                summary.active_documents
                            }
                            prefix={
                                <CheckCircle2
                                    size={21}
                                />
                            }
                        />

                    </Card>

                </Col>


                <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={4}
                >

                    <Card
                        loading={
                            summaryLoading
                        }
                        className="archive-stat-card"
                    >

                        <Statistic
                            title="Archived"
                            value={
                                summary.archived_documents
                            }
                            prefix={
                                <Archive
                                    size={21}
                                />
                            }
                        />

                    </Card>

                </Col>


                <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={4}
                >

                    <Card
                        loading={
                            summaryLoading
                        }
                        className="archive-stat-card"
                    >

                        <Statistic
                            title="Sensitive"
                            value={
                                summary.sensitive_documents
                            }
                            prefix={
                                <ShieldAlert
                                    size={21}
                                />
                            }
                        />

                    </Card>

                </Col>


                <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={4}
                >

                    <Card
                        loading={
                            summaryLoading
                        }
                        className="archive-stat-card"
                    >

                        <Statistic
                            title="Duplicates"
                            value={
                                summary.duplicate_documents
                            }
                            prefix={
                                <History
                                    size={21}
                                />
                            }
                        />

                    </Card>

                </Col>


                <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={4}
                >

                    <Card
                        loading={
                            summaryLoading
                        }
                        className="archive-stat-card"
                    >

                        <Statistic
                            title="Expired"
                            value={
                                summary.expired_documents
                            }
                            prefix={
                                <Clock3
                                    size={21}
                                />
                            }
                        />

                    </Card>

                </Col>

            </Row>


            {/* =================================================
                BREAKDOWN
            ================================================= */}

            <Row
                gutter={[
                    16,
                    16,
                ]}
                className="archive-breakdown-row"
            >

                <Col
                    xs={24}
                    lg={12}
                >

                    <Card
                        title={
                            <Space>
                                <BarChart3
                                    size={18}
                                />
                                Documents by Category
                            </Space>
                        }
                        className="archive-breakdown-card"
                    >

                        {
                            categoryBreakdown.length
                                ? (

                                    <div className="archive-category-grid">

                                        {
                                            categoryBreakdown.map(
                                                item => (

                                                    <div
                                                        key={
                                                            item.archive_category
                                                        }
                                                        className="archive-category-item"
                                                    >

                                                        <div>

                                                            <Tag
                                                                color={
                                                                    getCategoryColor(
                                                                        item.archive_category
                                                                    )
                                                                }
                                                            >
                                                                {
                                                                    getCategoryLabel(
                                                                        item.archive_category
                                                                    )
                                                                }
                                                            </Tag>

                                                        </div>

                                                        <strong>
                                                            {
                                                                item.total
                                                            }
                                                        </strong>

                                                    </div>

                                                )
                                            )
                                        }

                                    </div>

                                )
                                : (
                                    <Empty
                                        image={
                                            Empty.PRESENTED_IMAGE_SIMPLE
                                        }
                                        description="No category data"
                                    />
                                )
                        }

                    </Card>

                </Col>


                <Col
                    xs={24}
                    lg={12}
                >

                    <Card
                        title={
                            <Space>
                                <HardDrive
                                    size={18}
                                />
                                Academic Year Distribution
                            </Space>
                        }
                        className="archive-breakdown-card"
                    >

                        {
                            yearBreakdown.length
                                ? (

                                    <div>

                                        {
                                            yearBreakdown.map(
                                                item => (

                                                    <div
                                                        key={
                                                            item.academic_year_id
                                                        }
                                                        className="archive-year-breakdown"
                                                    >

                                                        <span>
                                                            {
                                                                item.academic_year__name
                                                                || "General"
                                                            }
                                                        </span>

                                                        <Badge
                                                            count={
                                                                item.total
                                                            }
                                                            overflowCount={
                                                                999999
                                                            }
                                                            showZero
                                                        />

                                                    </div>

                                                )
                                            )
                                        }

                                    </div>

                                )
                                : (
                                    <Empty
                                        image={
                                            Empty.PRESENTED_IMAGE_SIMPLE
                                        }
                                        description="No year data"
                                    />
                                )
                        }

                    </Card>

                </Col>

            </Row>


            {/* =================================================
                FILTERS
            ================================================= */}

            <Card
                className="archive-filter-card"
            >

                <div className="archive-filter-header">

                    <div>

                        <Title
                            level={4}
                            className="archive-section-title"
                        >
                            Archive Documents
                        </Title>

                        <Text
                            type="secondary"
                        >
                            {
                                totalDocuments.toLocaleString()
                            }{" "}
                            document
                            {
                                totalDocuments === 1
                                    ? ""
                                    : "s"
                            }{" "}
                            found for{" "}
                            <strong>
                                {
                                    selectedYear?.name
                                }
                            </strong>
                        </Text>

                    </div>

                    <Filter
                        size={19}
                    />

                </div>


                <Row
                    gutter={[
                        12,
                        12,
                    ]}
                    className="archive-filters"
                >

                    <Col
                        xs={24}
                        md={10}
                        lg={8}
                    >

                        <Input
                            allowClear
                            size="large"
                            value={
                                searchInput
                            }
                            prefix={
                                <Search
                                    size={17}
                                />
                            }
                            placeholder={
                                "Search title, reference, filename..."
                            }
                            onChange={
                                event =>
                                    setSearchInput(
                                        event.target.value
                                    )
                            }
                        />

                    </Col>


                    <Col
                        xs={24}
                        sm={12}
                        md={5}
                    >

                        <Select
                            allowClear
                            size="large"
                            className="archive-filter-select"
                            placeholder="Category"
                            value={
                                category
                                || undefined
                            }
                            onChange={
                                value =>
                                    setCategory(
                                        value
                                        || ""
                                    )
                            }
                        >

                            {
                                categoryOptions.map(
                                    option => (

                                        <Option
                                            key={
                                                option.value
                                            }
                                            value={
                                                option.value
                                            }
                                        >
                                            {
                                                option.label
                                            }
                                        </Option>

                                    )
                                )
                            }

                        </Select>

                    </Col>


                    <Col
                        xs={24}
                        sm={12}
                        md={5}
                    >

                        <Select
                            allowClear
                            loading={
                                documentTypesLoading
                            }
                            size="large"
                            className="archive-filter-select"
                            placeholder="Document Type"
                            value={
                                documentType
                                || undefined
                            }
                            onChange={
                                value =>
                                    setDocumentType(
                                        value
                                        || ""
                                    )
                            }
                        >

                            {
                                documentTypes.map(
                                    type => (

                                        <Option
                                            key={
                                                type.id
                                            }
                                            value={
                                                type.id
                                            }
                                        >
                                            {
                                                type.name
                                            }
                                        </Option>

                                    )
                                )
                            }

                        </Select>

                    </Col>


                    <Col
                        xs={24}
                        md={6}
                    >

                        <Select
                            size="large"
                            className="archive-filter-select"
                            value={
                                statusFilter
                            }
                            onChange={
                                value =>
                                    setStatusFilter(
                                        value
                                    )
                            }
                        >

                            <Option
                                value="active"
                            >
                                Active Documents
                            </Option>

                            <Option
                                value="archived"
                            >
                                Archived Documents
                            </Option>

                            <Option
                                value="all"
                            >
                                All Documents
                            </Option>

                        </Select>

                    </Col>

                </Row>


                {
                    (
                        search
                        || category
                        || documentType
                        || statusFilter !== "active"
                    )
                        ? (

                            <div className="archive-active-filters">

                                <Text
                                    type="secondary"
                                >
                                    Active filters:
                                </Text>

                                {
                                    search
                                        ? (
                                            <Tag
                                                closable
                                                onClose={() =>
                                                    setSearchInput(
                                                        ""
                                                    )
                                                }
                                            >
                                                Search:{" "}
                                                {
                                                    search
                                                }
                                            </Tag>
                                        )
                                        : null
                                }

                                {
                                    category
                                        ? (
                                            <Tag
                                                closable
                                                onClose={() =>
                                                    setCategory(
                                                        ""
                                                    )
                                                }
                                            >
                                                {
                                                    getCategoryLabel(
                                                        category
                                                    )
                                                }
                                            </Tag>
                                        )
                                        : null
                                }

                                {
                                    documentType
                                        ? (
                                            <Tag
                                                closable
                                                onClose={() =>
                                                    setDocumentType(
                                                        ""
                                                    )
                                                }
                                            >
                                                Document Type
                                            </Tag>
                                        )
                                        : null
                                }

                                {
                                    statusFilter !==
                                    "active"
                                        ? (
                                            <Tag
                                                closable
                                                onClose={() =>
                                                    setStatusFilter(
                                                        "active"
                                                    )
                                                }
                                            >
                                                {
                                                    statusFilter
                                                }
                                            </Tag>
                                        )
                                        : null
                                }

                            </div>

                        )
                        : null
                }

            </Card>


            {/* =================================================
                TABLE
            ================================================= */}

            <Card
                className="archive-table-card"
                bodyStyle={{
                    padding:
                        0,
                }}
            >

                <Table
                    rowKey="id"
                    columns={
                        columns
                    }
                    dataSource={
                        documents
                    }
                    loading={
                        loading
                    }
                    pagination={
                        false
                    }
                    scroll={{
                        x:
                            1250,
                    }}
                    locale={{
                        emptyText: (
                            <Empty
                                description={
                                    search
                                        ? "No documents match your search."
                                        : "No archive documents found for this academic year."
                                }
                            />
                        ),
                    }}
                />

                {
                    totalDocuments > 0
                        ? (

                            <div className="archive-pagination-wrapper">

                                <Text
                                    type="secondary"
                                >
                                    Page{" "}
                                    {
                                        currentPage
                                    }{" "}
                                    of{" "}
                                    {
                                        totalPages
                                    }
                                </Text>

                                <Pagination
                                    current={
                                        currentPage
                                    }
                                    pageSize={
                                        PAGE_SIZE
                                    }
                                    total={
                                        totalDocuments
                                    }
                                    showSizeChanger={
                                        false
                                    }
                                    showQuickJumper
                                    onChange={
                                        page =>
                                            setCurrentPage(
                                                page
                                            )
                                    }
                                />

                            </div>

                        )
                        : null
                }

            </Card>


            {/* =================================================
                UPLOAD / EDIT FORM
            ================================================= */}

            <ArchiveDocumentForm
                open={
                    formOpen
                }
                document={
                    editingDocument
                }
                selectedYear={
                    selectedYear
                }
                documentTypes={
                    documentTypes
                }
                onClose={() => {

                    setFormOpen(
                        false
                    );

                    setEditingDocument(
                        null
                    );

                }}
                onSuccess={
                    handleFormSuccess
                }
            />


            {/* =================================================
                DETAILS
            ================================================= */}

            <ArchiveDocumentDetails
                open={
                    detailsOpen
                }
                document={
                    selectedDocument
                }
                onClose={() => {

                    setDetailsOpen(
                        false
                    );

                    setSelectedDocument(
                        null
                    );

                }}
                onDownload={
                    handleDownload
                }
            />

        </div>

    );

};


export default SchoolArchiveDashboard;

